import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Plus, LogOut, ExternalLink } from "lucide-react";
import Logo from "@/components/Logo";
import api, { formatApiError } from "@/lib/api";

const STATUSES = ["new", "in-progress", "replied", "closed"];
const EMPTY_ARTICLE = { title: "", category: "Insights", excerpt: "", content: "", cover_image: "", author: "ARMS Team", published: true };

export default function AdminDashboard() {
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("enquiries");
  const [enquiries, setEnquiries] = useState([]);
  const [articles, setArticles] = useState([]);
  const [editing, setEditing] = useState(null); // null | EMPTY_ARTICLE | article
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => navigate("/admin/login"));
  }, [navigate]);

  useEffect(() => {
    if (!user) return;
    api.get("/enquiries").then((r) => setEnquiries(r.data)).catch(() => {});
    api.get("/articles/admin").then((r) => setArticles(r.data)).catch(() => {});
  }, [user]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/enquiries/${id}`, { status });
      setEnquiries((prev) => prev.map((e) => (e.id === id ? { ...e, status } : e)));
      toast.success("Status updated");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeEnquiry = async (id) => {
    try {
      await api.delete(`/enquiries/${id}`);
      setEnquiries((prev) => prev.filter((e) => e.id !== id));
      toast.success("Enquiry deleted");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveArticle = async (e) => {
    e.preventDefault();
    try {
      if (editing.id) {
        const r = await api.put(`/articles/${editing.id}`, editing);
        setArticles((prev) => prev.map((a) => (a.id === editing.id ? r.data : a)));
        toast.success("Article updated");
      } else {
        const r = await api.post("/articles", editing);
        setArticles((prev) => [r.data, ...prev]);
        toast.success("Article published");
      }
      setEditing(null);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeArticle = async (id) => {
    try {
      await api.delete(`/articles/${id}`);
      setArticles((prev) => prev.filter((a) => a.id !== id));
      toast.success("Article deleted");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  if (!user) return <div className="flex min-h-screen items-center justify-center bg-slate-50 text-slate-500">Loading…</div>;

  const newCount = enquiries.filter((e) => e.status === "new").length;
  const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 focus:border-teal focus:outline-none";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur-xl">
        <div className="container-arms flex h-16 items-center justify-between">
          <Link to="/" data-testid="admin-logo-link"><Logo light={false} full={false} className="!h-8" /></Link>
          <div className="flex items-center gap-3">
            <Link to="/" className="hidden sm:inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-teal" data-testid="admin-view-site-link">
              <ExternalLink size={14} /> View site
            </Link>
            <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:border-teal hover:text-teal">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="container-arms py-10">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-navy">Dashboard</h1>
        <p className="mt-1 text-sm text-slate-500">Signed in as {user.email}</p>

        <div className="mt-8 grid gap-4 sm:grid-cols-3" data-testid="admin-stats">
          {[[enquiries.length, "Total enquiries"], [newCount, "New enquiries"], [articles.length, "Articles"]].map(([v, l]) => (
            <div key={l} className="rounded-2xl border border-slate-200 bg-white p-6">
              <p className="font-display text-3xl font-extrabold text-navy">{v}</p>
              <p className="mt-1 text-sm text-slate-500">{l}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex gap-2" role="tablist">
          {[["enquiries", "Enquiries"], ["articles", "Articles"]].map(([key, label]) => (
            <button
              key={key}
              role="tab"
              aria-selected={tab === key}
              onClick={() => setTab(key)}
              data-testid={`admin-tab-${key}`}
              className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${tab === key ? "bg-navy text-white" : "bg-white text-slate-600 border border-slate-200"}`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "enquiries" && (
          <div className="mt-6 space-y-4" data-testid="admin-enquiries-panel">
            {enquiries.length === 0 && (
              <p className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500" data-testid="admin-enquiries-empty">
                No enquiries yet — they'll appear here when the contact form is used.
              </p>
            )}
            {enquiries.map((e) => (
              <article key={e.id} className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={`admin-enquiry-${e.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="font-bold text-navy">{e.name} <span className="font-normal text-slate-500">· {e.email}</span></p>
                    <p className="mt-0.5 text-sm text-slate-500">
                      {e.company || "—"} · {e.service} {e.sector ? `· ${e.sector}` : ""} · {new Date(e.created_at).toLocaleString("en-GB")}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={e.status}
                      onChange={(ev) => setStatus(e.id, ev.target.value)}
                      data-testid={`admin-enquiry-status-${e.id}`}
                      className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700"
                    >
                      {STATUSES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                    <button onClick={() => removeEnquiry(e.id)} data-testid={`admin-enquiry-delete-${e.id}`} aria-label="Delete enquiry" className="rounded-full p-2 text-slate-400 hover:bg-red-50 hover:text-red-600">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                <p className="mt-4 whitespace-pre-wrap rounded-xl bg-slate-50 p-4 text-sm text-slate-700">{e.message}</p>
              </article>
            ))}
          </div>
        )}

        {tab === "articles" && (
          <div className="mt-6" data-testid="admin-articles-panel">
            {editing ? (
              <form onSubmit={saveArticle} className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6" data-testid="admin-article-form">
                <h2 className="text-lg font-bold text-navy">{editing.id ? "Edit article" : "New article"}</h2>
                <div>
                  <label className="mb-1 block text-sm font-semibold text-slate-700" htmlFor="article-title">Title *</label>
                  <input id="article-title" data-testid="admin-article-title-input" required className={inputCls} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-semibold text-slate-700" htmlFor="article-category">Category</label>
                    <input id="article-category" data-testid="admin-article-category-input" className={inputCls} value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-semibold text-slate-700" htmlFor="article-author">Author</label>
                    <input id="article-author" data-testid="admin-article-author-input" className={inputCls} value={editing.author} onChange={(e) => setEditing({ ...editing, author: e.target.value })} />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-semibold text-slate-700" htmlFor="article-cover">Cover image URL</label>
                  <input id="article-cover" data-testid="admin-article-cover-input" className={inputCls} value={editing.cover_image} onChange={(e) => setEditing({ ...editing, cover_image: e.target.value })} placeholder="https://…" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-semibold text-slate-700" htmlFor="article-excerpt">Excerpt</label>
                  <textarea id="article-excerpt" data-testid="admin-article-excerpt-input" rows={2} className={inputCls} value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-semibold text-slate-700" htmlFor="article-content">Content (blank line between paragraphs)</label>
                  <textarea id="article-content" data-testid="admin-article-content-input" rows={10} className={inputCls} value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} />
                </div>
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <input type="checkbox" data-testid="admin-article-published-checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} className="h-4 w-4 accent-teal-600" />
                  Published (visible on the site)
                </label>
                <div className="flex gap-3">
                  <button type="submit" data-testid="admin-article-save-button" className="rounded-full bg-navy px-6 py-2.5 text-sm font-bold text-white hover:bg-teal">
                    {editing.id ? "Save changes" : "Publish article"}
                  </button>
                  <button type="button" onClick={() => setEditing(null)} data-testid="admin-article-cancel-button" className="rounded-full border border-slate-200 px-6 py-2.5 text-sm font-semibold text-slate-600">
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <>
                <button
                  onClick={() => setEditing({ ...EMPTY_ARTICLE })}
                  data-testid="admin-article-create-button"
                  className="mb-4 inline-flex items-center gap-2 rounded-full bg-teal px-5 py-2.5 text-sm font-bold text-white hover:bg-navy"
                >
                  <Plus size={16} /> New article
                </button>
                <div className="space-y-3">
                  {articles.length === 0 && (
                    <p className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500" data-testid="admin-articles-empty">
                      No articles yet. Create your first insight above.
                    </p>
                  )}
                  {articles.map((a) => (
                    <article key={a.id} className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid={`admin-article-${a.id}`}>
                      <div className="min-w-0">
                        <p className="truncate font-bold text-navy">{a.title}</p>
                        <p className="mt-0.5 text-xs text-slate-500">
                          {a.category} · {a.author} · {a.published ? "Published" : "Draft"}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Link to={`/insights/${a.slug}`} className="rounded-full border border-slate-200 px-4 py-1.5 text-xs font-semibold text-slate-600 hover:border-teal" data-testid={`admin-article-view-${a.id}`}>View</Link>
                        <button onClick={() => setEditing(a)} data-testid={`admin-article-edit-${a.id}`} className="rounded-full border border-slate-200 px-4 py-1.5 text-xs font-semibold text-slate-600 hover:border-teal">Edit</button>
                        <button onClick={() => removeArticle(a.id)} data-testid={`admin-article-delete-${a.id}`} aria-label="Delete article" className="rounded-full p-2 text-slate-400 hover:bg-red-50 hover:text-red-600">
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
